/* One password form, used by both shells.

   It existed only in the employee portal, so an Admin or an HR Manager had no way to
   change their own password at all — while `POST /users/{id}/reset-password` refuses a
   self-reset with "use Change password instead", naming a button that was not on any
   screen they could reach. Nobody may reset their own account, so for an HR role that
   left no route to a new password except asking a second Admin to issue a one-time one.

   `forced` is the must_change_password case: the dialog cannot be dismissed, because the
   API refuses everything but /auth/me, /auth/change-password and /auth/logout until it
   is answered, and a dismissible dialog there just yields an app that refuses every click. */
import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const EMPTY = { current: "", next: "", confirm: "" };

export default function ChangePasswordDialog({ open, onOpenChange, forced = false, onChanged }) {
  const [pw, setPw] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (pw.next !== pw.confirm) { toast.error("Passwords don't match"); return; }
    if (pw.next.length < 6) { toast.error("Min 6 chars"); return; }
    setSaving(true);
    try {
      // Changing a password outdates every token the account is holding. The replacement
      // rides back on the response so the person who just did it keeps the screen they
      // are standing on; anyone else signed in as them does not, which is the point.
      const r = await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      if (r.data?.token) localStorage.setItem("token", r.data.token);
      toast.success("Password updated");
      setPw(EMPTY);
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const hold = (e) => { if (forced) e.preventDefault(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && forced) return; if (!v) setPw(EMPTY); onOpenChange(v); }}>
      <DialogContent data-testid="pw-dialog"
                     onEscapeKeyDown={hold} onPointerDownOutside={hold} onInteractOutside={hold}>
        <DialogHeader>
          <DialogTitle>{forced ? "Set your password" : "Change password"}</DialogTitle>
          <DialogDescription>Use at least 6 characters. You'll stay logged in after updating.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Current password</Label><Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" /></div>
          <div><Label>New password</Label><Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-new" /></div>
          <div><Label>Confirm new password</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={forced}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="pw-submit">{saving ? "Updating…" : "Update"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
